// Example Data Provider
// Centralized provider for all mock/example data used throughout the application

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useSampleData } from '../../hooks/useSampleData';

// Import all mock data
import { mockGurus } from './mockGurus';
import { mockRecipes } from './mockRecipes';
import { mockMealPlans } from './mockMealPlans';
import { mockDietPlans } from './mockDietPlans';
import { mockProducts } from './mockProducts';
import { mockSessions } from './mockSessions';
import { mockReviews } from './mockReviews';
import { mockLeaderboards } from './mockLeaderboards';
import { mockContributors } from './mockContributors';
import { mockTrustScores } from './mockTrustScores';
import { mockAnalytics } from './mockAnalytics';
import { mockContent } from './mockContent';
import { mockUsers } from './mockUsers';
import { mockHealthData } from './mockHealthData';
import { mockMedications } from './mockMedications';
import { mockFoods } from './mockFoods';
import { mockSymptoms } from './mockSymptoms';
import { mockConditions } from './mockConditions';
import { mockDeviceData } from './mockDeviceData';
import { mockSponsors, mockSponsoredProducts, mockSponsorCoupons } from './mockSponsors';
import { exampleRecipes, exampleContributors } from './recipes';
import { exampleSponsors, exampleSponsoredProducts, exampleCoupons, exampleInteractions } from './sponsorship';
import { exampleReferralStats } from './social';
import { exampleActivities } from './activities';
import { exampleQuickActions } from './quickActions';
import { exampleHealthMetrics } from './healthMetrics';
import { exampleInnovativeFeatures } from './innovativeFeatures';
import { exampleNavigationItems } from './navigationItems';
import { exampleFeatureInspirations, exampleContributorCategories } from './aboutData';
import { exampleFoodCorrelations } from './foodCorrelations';
import { exampleLiveSessions } from './liveSessions';
import { exampleConnectedDevices, exampleTelemetryData } from './deviceData';
import { exampleTrustScoreData } from './trustScoreData';
import { exampleReviews } from './reviewData';
import { examplePredictionModels } from './predictiveInsightsData';
import { exampleSessions, exampleEarnings } from './guruDashboardData';

// Example Data Keys
export const EXAMPLE_DATA_KEYS = {
  GURUS: 'gurus',
  RECIPES: 'recipes',
  MEAL_PLANS: 'mealPlans',
  DIET_PLANS: 'dietPlans',
  PRODUCTS: 'products',
  SESSIONS: 'sessions',
  REVIEWS: 'reviews',
  LEADERBOARDS: 'leaderboards',
  CONTRIBUTORS: 'contributors',
  TRUST_SCORES: 'trustScores',
  ANALYTICS: 'analytics',
  CONTENT: 'content',
  USERS: 'users',
  HEALTH_DATA: 'healthData',
  MEDICATIONS: 'medications',
  FOODS: 'foods',
  SYMPTOMS: 'symptoms',
  CONDITIONS: 'conditions',
  DEVICE_DATA: 'deviceData',
  SPONSORS: 'sponsors',
  SPONSORED_PRODUCTS: 'sponsoredProducts',
  SPONSOR_COUPONS: 'sponsorCoupons',
  RECIPES_NEW: 'recipesNew',
  CONTRIBUTORS_NEW: 'contributorsNew',
  SPONSORS_NEW: 'sponsorsNew',
  SPONSORED_PRODUCTS_NEW: 'sponsoredProductsNew',
  COUPONS_NEW: 'couponsNew',
  INTERACTIONS: 'interactions',
  REFERRAL_STATS: 'referralStats',
  ACTIVITIES: 'activities',
  QUICK_ACTIONS: 'quickActions',
  HEALTH_METRICS: 'healthMetrics',
  INNOVATIVE_FEATURES: 'innovativeFeatures',
  NAVIGATION_ITEMS: 'navigationItems',
  FEATURE_INSPIRATIONS: 'featureInspirations',
  CONTRIBUTOR_CATEGORIES: 'contributorCategories',
  FOOD_CORRELATIONS: 'foodCorrelations',
  LIVE_SESSIONS: 'liveSessions',
  CONNECTED_DEVICES: 'connectedDevices',
  TELEMETRY_DATA: 'telemetryData',
  TRUST_SCORE_DATA: 'trustScoreData',
  PREDICTION_MODELS: 'predictionModels',
  GURU_SESSIONS: 'guruSessions',
  GURU_REVIEWS: 'guruReviews',
  GURU_EARNINGS: 'guruEarnings'
} as const;

export type ExampleDataKey = typeof EXAMPLE_DATA_KEYS[keyof typeof EXAMPLE_DATA_KEYS];

// Example Data Registry
const EXAMPLE_DATA_REGISTRY: Record<ExampleDataKey, any[]> = {
  [EXAMPLE_DATA_KEYS.GURUS]: mockGurus,
  [EXAMPLE_DATA_KEYS.RECIPES]: mockRecipes,
  [EXAMPLE_DATA_KEYS.MEAL_PLANS]: mockMealPlans,
  [EXAMPLE_DATA_KEYS.DIET_PLANS]: mockDietPlans,
  [EXAMPLE_DATA_KEYS.PRODUCTS]: mockProducts,
  [EXAMPLE_DATA_KEYS.SESSIONS]: mockSessions,
  [EXAMPLE_DATA_KEYS.LEADERBOARDS]: mockLeaderboards,
  [EXAMPLE_DATA_KEYS.CONTRIBUTORS]: mockContributors,
  [EXAMPLE_DATA_KEYS.TRUST_SCORES]: [mockTrustScores],
  [EXAMPLE_DATA_KEYS.ANALYTICS]: [mockAnalytics],
  [EXAMPLE_DATA_KEYS.CONTENT]: mockContent,
  [EXAMPLE_DATA_KEYS.USERS]: mockUsers,
  [EXAMPLE_DATA_KEYS.HEALTH_DATA]: [mockHealthData],
  [EXAMPLE_DATA_KEYS.MEDICATIONS]: mockMedications,
  [EXAMPLE_DATA_KEYS.FOODS]: mockFoods,
  [EXAMPLE_DATA_KEYS.SYMPTOMS]: mockSymptoms,
  [EXAMPLE_DATA_KEYS.CONDITIONS]: mockConditions,
  [EXAMPLE_DATA_KEYS.DEVICE_DATA]: mockDeviceData,
  [EXAMPLE_DATA_KEYS.SPONSORS]: mockSponsors,
  [EXAMPLE_DATA_KEYS.SPONSORED_PRODUCTS]: mockSponsoredProducts,
  [EXAMPLE_DATA_KEYS.SPONSOR_COUPONS]: mockSponsorCoupons,
  [EXAMPLE_DATA_KEYS.RECIPES_NEW]: exampleRecipes,
  [EXAMPLE_DATA_KEYS.CONTRIBUTORS_NEW]: exampleContributors,
  [EXAMPLE_DATA_KEYS.SPONSORS_NEW]: exampleSponsors,
  [EXAMPLE_DATA_KEYS.SPONSORED_PRODUCTS_NEW]: exampleSponsoredProducts,
  [EXAMPLE_DATA_KEYS.COUPONS_NEW]: exampleCoupons,
  [EXAMPLE_DATA_KEYS.INTERACTIONS]: exampleInteractions,
  [EXAMPLE_DATA_KEYS.REFERRAL_STATS]: [exampleReferralStats],
  [EXAMPLE_DATA_KEYS.ACTIVITIES]: exampleActivities,
  [EXAMPLE_DATA_KEYS.QUICK_ACTIONS]: exampleQuickActions,
  [EXAMPLE_DATA_KEYS.HEALTH_METRICS]: exampleHealthMetrics,
  [EXAMPLE_DATA_KEYS.INNOVATIVE_FEATURES]: exampleInnovativeFeatures,
  [EXAMPLE_DATA_KEYS.NAVIGATION_ITEMS]: exampleNavigationItems,
  [EXAMPLE_DATA_KEYS.FEATURE_INSPIRATIONS]: exampleFeatureInspirations,
  [EXAMPLE_DATA_KEYS.CONTRIBUTOR_CATEGORIES]: exampleContributorCategories,
  [EXAMPLE_DATA_KEYS.FOOD_CORRELATIONS]: exampleFoodCorrelations,
  [EXAMPLE_DATA_KEYS.LIVE_SESSIONS]: exampleLiveSessions,
  [EXAMPLE_DATA_KEYS.CONNECTED_DEVICES]: exampleConnectedDevices,
  [EXAMPLE_DATA_KEYS.TELEMETRY_DATA]: exampleTelemetryData,
  [EXAMPLE_DATA_KEYS.TRUST_SCORE_DATA]: [exampleTrustScoreData],
  [EXAMPLE_DATA_KEYS.PREDICTION_MODELS]: examplePredictionModels,
  [EXAMPLE_DATA_KEYS.GURU_SESSIONS]: exampleSessions,
  [EXAMPLE_DATA_KEYS.GURU_REVIEWS]: exampleReviews,
  [EXAMPLE_DATA_KEYS.GURU_EARNINGS]: exampleEarnings
};

// Context Interface
interface ExampleDataContextType {
  // Core data access methods
  getExampleData(key: ExampleDataKey): any[];
  hasExampleData(key: ExampleDataKey): boolean;
  clearExampleData(key: ExampleDataKey): void;
  getAllExampleDataKeys(): ExampleDataKey[];
  
  // Feature-specific data access
  getGurus(): typeof mockGurus;
  getRecipes(): typeof mockRecipes;
  getMealPlans(): typeof mockMealPlans;
  getDietPlans(): typeof mockDietPlans;
  getProducts(): typeof mockProducts;
  getSessions(): typeof mockSessions;
  getReviews(): typeof exampleReviews;
  getLeaderboards(): typeof mockLeaderboards;
  getContributors(): typeof mockContributors;
  getTrustScores(): typeof mockTrustScores;
  getAnalytics(): typeof mockAnalytics;
  getContent(): typeof mockContent;
  getUsers(): typeof mockUsers;
  getHealthData(): typeof mockHealthData;
  getMedications(): typeof mockMedications;
  getFoods(): typeof mockFoods;
  getSymptoms(): typeof mockSymptoms;
  getConditions(): typeof mockConditions;
  getDeviceData(): typeof mockDeviceData;
  getSponsors(): typeof mockSponsors;
  getSponsoredProducts(): typeof mockSponsoredProducts;
  getSponsorCoupons(): typeof mockSponsorCoupons;
  getRecipesNew(): typeof exampleRecipes;
  getContributorsNew(): typeof exampleContributors;
  getSponsorsNew(): typeof exampleSponsors;
  getSponsoredProductsNew(): typeof exampleSponsoredProducts;
  getCouponsNew(): typeof exampleCoupons;
  getInteractions(): typeof exampleInteractions;
  getReferralStats(): typeof exampleReferralStats;
  getActivities(): typeof exampleActivities;
  getQuickActions(): typeof exampleQuickActions;
  getHealthMetrics(): typeof exampleHealthMetrics;
  getInnovativeFeatures(): typeof exampleInnovativeFeatures;
  getNavigationItems(): typeof exampleNavigationItems;
  getFeatureInspirations(): typeof exampleFeatureInspirations;
  getContributorCategories(): typeof exampleContributorCategories;
  getFoodCorrelations(): typeof exampleFoodCorrelations;
  getLiveSessions(): typeof exampleLiveSessions;
  getConnectedDevices(): typeof exampleConnectedDevices;
  getTelemetryData(): typeof exampleTelemetryData;
  getTrustScoreData(): typeof exampleTrustScoreData;
  getPredictionModels(): typeof examplePredictionModels;
  getGuruSessions(): typeof exampleSessions;
  getGuruReviews(): typeof exampleReviews;
  getGuruEarnings(): typeof exampleEarnings;
  
  // Utility methods
  isExampleDataEnabled(): boolean;
  shouldShowExampleData(feature: string): boolean;
  getExampleDataContext(feature: string): any;
}

// Create Context
const ExampleDataContext = createContext<ExampleDataContextType | undefined>(undefined);

// Provider Props
interface ExampleDataProviderProps {
  children: ReactNode;
}

// Provider Component
export const ExampleDataProvider: React.FC<ExampleDataProviderProps> = ({ children }) => {
  const { shouldShowSampleData } = useSampleData();
  const [enabledDataKeys, setEnabledDataKeys] = useState<Set<ExampleDataKey>>(new Set());

  // Initialize enabled data keys based on sample data preferences
  useEffect(() => {
    const enabledKeys = new Set<ExampleDataKey>();
    
    // Check which example data should be enabled based on sample data settings
    if (shouldShowSampleData('guru-connect')) {
      enabledKeys.add(EXAMPLE_DATA_KEYS.GURUS);
      enabledKeys.add(EXAMPLE_DATA_KEYS.SESSIONS);
      enabledKeys.add(EXAMPLE_DATA_KEYS.REVIEWS);
    }
    
    if (shouldShowSampleData('food-tracking')) {
      enabledKeys.add(EXAMPLE_DATA_KEYS.FOODS);
      enabledKeys.add(EXAMPLE_DATA_KEYS.RECIPES);
      enabledKeys.add(EXAMPLE_DATA_KEYS.MEAL_PLANS);
      enabledKeys.add(EXAMPLE_DATA_KEYS.DIET_PLANS);
    }
    
    if (shouldShowSampleData('medication-management')) {
      enabledKeys.add(EXAMPLE_DATA_KEYS.MEDICATIONS);
    }
    
    if (shouldShowSampleData('health-metrics')) {
      enabledKeys.add(EXAMPLE_DATA_KEYS.HEALTH_DATA);
      enabledKeys.add(EXAMPLE_DATA_KEYS.SYMPTOMS);
      enabledKeys.add(EXAMPLE_DATA_KEYS.CONDITIONS);
      enabledKeys.add(EXAMPLE_DATA_KEYS.DEVICE_DATA);
      enabledKeys.add(EXAMPLE_DATA_KEYS.HEALTH_METRICS);
    }
    
    // Always enable some core example data for demo purposes
    enabledKeys.add(EXAMPLE_DATA_KEYS.USERS);
    enabledKeys.add(EXAMPLE_DATA_KEYS.ANALYTICS);
    enabledKeys.add(EXAMPLE_DATA_KEYS.CONTENT);
    enabledKeys.add(EXAMPLE_DATA_KEYS.TRUST_SCORES);
    enabledKeys.add(EXAMPLE_DATA_KEYS.LEADERBOARDS);
    enabledKeys.add(EXAMPLE_DATA_KEYS.CONTRIBUTORS);
    enabledKeys.add(EXAMPLE_DATA_KEYS.PRODUCTS);
    enabledKeys.add(EXAMPLE_DATA_KEYS.SPONSORS);
    enabledKeys.add(EXAMPLE_DATA_KEYS.SPONSORED_PRODUCTS);
    enabledKeys.add(EXAMPLE_DATA_KEYS.SPONSOR_COUPONS);
    
    setEnabledDataKeys(enabledKeys);
  }, [shouldShowSampleData]);

  // Core data access methods
  const getExampleData = (key: ExampleDataKey): any[] => {
    if (!enabledDataKeys.has(key)) {
      return [];
    }
    return EXAMPLE_DATA_REGISTRY[key] || [];
  };

  const hasExampleData = (key: ExampleDataKey): boolean => {
    return enabledDataKeys.has(key) && EXAMPLE_DATA_REGISTRY[key]?.length > 0;
  };

  const clearExampleData = (key: ExampleDataKey): void => {
    setEnabledDataKeys(prev => {
      const newSet = new Set(prev);
      newSet.delete(key);
      return newSet;
    });
  };

  const getAllExampleDataKeys = (): ExampleDataKey[] => {
    return Array.from(enabledDataKeys);
  };

  // Feature-specific data access methods
  const getGurus = () => getExampleData(EXAMPLE_DATA_KEYS.GURUS);
  const getRecipes = () => getExampleData(EXAMPLE_DATA_KEYS.RECIPES);
  const getMealPlans = () => getExampleData(EXAMPLE_DATA_KEYS.MEAL_PLANS);
  const getDietPlans = () => getExampleData(EXAMPLE_DATA_KEYS.DIET_PLANS);
  const getProducts = () => getExampleData(EXAMPLE_DATA_KEYS.PRODUCTS);
  const getSessions = () => getExampleData(EXAMPLE_DATA_KEYS.SESSIONS);
  const getReviews = () => getExampleData(EXAMPLE_DATA_KEYS.REVIEWS);
  const getLeaderboards = () => getExampleData(EXAMPLE_DATA_KEYS.LEADERBOARDS);
  const getContributors = () => getExampleData(EXAMPLE_DATA_KEYS.CONTRIBUTORS);
  const getTrustScores = () => getExampleData(EXAMPLE_DATA_KEYS.TRUST_SCORES)[0];
  const getAnalytics = () => getExampleData(EXAMPLE_DATA_KEYS.ANALYTICS)[0];
  const getContent = () => getExampleData(EXAMPLE_DATA_KEYS.CONTENT);
  const getUsers = () => getExampleData(EXAMPLE_DATA_KEYS.USERS);
  const getHealthData = () => getExampleData(EXAMPLE_DATA_KEYS.HEALTH_DATA)[0];
  const getMedications = () => getExampleData(EXAMPLE_DATA_KEYS.MEDICATIONS);
  const getFoods = () => getExampleData(EXAMPLE_DATA_KEYS.FOODS);
  const getSymptoms = () => getExampleData(EXAMPLE_DATA_KEYS.SYMPTOMS);
  const getConditions = () => getExampleData(EXAMPLE_DATA_KEYS.CONDITIONS);
  const getDeviceData = () => getExampleData(EXAMPLE_DATA_KEYS.DEVICE_DATA);
  const getSponsors = () => getExampleData(EXAMPLE_DATA_KEYS.SPONSORS);
  const getSponsoredProducts = () => getExampleData(EXAMPLE_DATA_KEYS.SPONSORED_PRODUCTS);
  const getSponsorCoupons = () => getExampleData(EXAMPLE_DATA_KEYS.SPONSOR_COUPONS);
  const getRecipesNew = () => getExampleData(EXAMPLE_DATA_KEYS.RECIPES_NEW);
  const getContributorsNew = () => getExampleData(EXAMPLE_DATA_KEYS.CONTRIBUTORS_NEW);
  const getSponsorsNew = () => getExampleData(EXAMPLE_DATA_KEYS.SPONSORS_NEW);
  const getSponsoredProductsNew = () => getExampleData(EXAMPLE_DATA_KEYS.SPONSORED_PRODUCTS_NEW);
  const getCouponsNew = () => getExampleData(EXAMPLE_DATA_KEYS.COUPONS_NEW);
  const getInteractions = () => getExampleData(EXAMPLE_DATA_KEYS.INTERACTIONS);
  const getReferralStats = () => getExampleData(EXAMPLE_DATA_KEYS.REFERRAL_STATS)[0];
  const getActivities = () => getExampleData(EXAMPLE_DATA_KEYS.ACTIVITIES);
  const getQuickActions = () => getExampleData(EXAMPLE_DATA_KEYS.QUICK_ACTIONS);
  const getHealthMetrics = () => getExampleData(EXAMPLE_DATA_KEYS.HEALTH_METRICS);
  const getInnovativeFeatures = () => getExampleData(EXAMPLE_DATA_KEYS.INNOVATIVE_FEATURES);
  const getNavigationItems = () => getExampleData(EXAMPLE_DATA_KEYS.NAVIGATION_ITEMS);
  const getFeatureInspirations = () => getExampleData(EXAMPLE_DATA_KEYS.FEATURE_INSPIRATIONS);
  const getContributorCategories = () => getExampleData(EXAMPLE_DATA_KEYS.CONTRIBUTOR_CATEGORIES);
  const getFoodCorrelations = () => getExampleData(EXAMPLE_DATA_KEYS.FOOD_CORRELATIONS);
  const getLiveSessions = () => getExampleData(EXAMPLE_DATA_KEYS.LIVE_SESSIONS);
  const getConnectedDevices = () => getExampleData(EXAMPLE_DATA_KEYS.CONNECTED_DEVICES);
  const getTelemetryData = () => getExampleData(EXAMPLE_DATA_KEYS.TELEMETRY_DATA);
  const getTrustScoreData = () => getExampleData(EXAMPLE_DATA_KEYS.TRUST_SCORE_DATA)[0];
  const getPredictionModels = () => getExampleData(EXAMPLE_DATA_KEYS.PREDICTION_MODELS);
  const getGuruSessions = () => getExampleData(EXAMPLE_DATA_KEYS.GURU_SESSIONS);
  const getGuruReviews = () => getExampleData(EXAMPLE_DATA_KEYS.GURU_REVIEWS);
  const getGuruEarnings = () => getExampleData(EXAMPLE_DATA_KEYS.GURU_EARNINGS);

  // Utility methods
  const isExampleDataEnabled = (): boolean => {
    return enabledDataKeys.size > 0;
  };

  const shouldShowExampleData = (feature: string): boolean => {
    return shouldShowSampleData(feature);
  };

  const getExampleDataContext = (feature: string): any => {
    // Return appropriate context based on feature
    switch (feature) {
      case 'guru-connect':
        return { gurus: getGurus(), sessions: getSessions(), reviews: getReviews() };
      case 'food-tracking':
        return { foods: getFoods(), recipes: getRecipes(), mealPlans: getMealPlans() };
      case 'medication-management':
        return { medications: getMedications() };
      case 'health-metrics':
        return { healthData: getHealthData(), symptoms: getSymptoms(), conditions: getConditions() };
      default:
        return {};
    }
  };

  const contextValue: ExampleDataContextType = {
    getExampleData,
    hasExampleData,
    clearExampleData,
    getAllExampleDataKeys,
    getGurus,
    getRecipes,
    getMealPlans,
    getDietPlans,
    getProducts,
    getSessions,
    getReviews,
    getLeaderboards,
    getContributors,
    getTrustScores,
    getAnalytics,
    getContent,
    getUsers,
    getHealthData,
    getMedications,
    getFoods,
    getSymptoms,
    getConditions,
    getDeviceData,
    getSponsors,
    getSponsoredProducts,
    getSponsorCoupons,
    getRecipesNew,
    getContributorsNew,
    getSponsorsNew,
    getSponsoredProductsNew,
    getCouponsNew,
    getInteractions,
    getReferralStats,
    getActivities,
    getQuickActions,
    getHealthMetrics,
    getInnovativeFeatures,
    getNavigationItems,
    getFeatureInspirations,
    getContributorCategories,
    getFoodCorrelations,
    getLiveSessions,
    getConnectedDevices,
    getTelemetryData,
    getTrustScoreData,
    getPredictionModels,
    getGuruSessions,
    getGuruReviews,
    getGuruEarnings,
    isExampleDataEnabled,
    shouldShowExampleData,
    getExampleDataContext
  };

  return (
    <ExampleDataContext.Provider value={contextValue}>
      {children}
    </ExampleDataContext.Provider>
  );
};

// Hook to use the Example Data Provider
export const useExampleData = (): ExampleDataContextType => {
  const context = useContext(ExampleDataContext);
  if (context === undefined) {
    throw new Error('useExampleData must be used within an ExampleDataProvider');
  }
  return context;
}; 